/**
 * AppUpgradeStatusesAgGridHook
 *
 * Grid for the "App Upgrades" tab of /ota/status — one row per device per
 * upgrade campaign, so this table grows with both fleet size and campaign
 * count. Uses AG Grid's server-side row model for the same reason as the
 * other OTA/parameter grids. See
 * PlatformWeb.ApplicationUpgradeStatusLive.Index for the query side.
 */

import { mountServerSideGrid } from "../base/ServerSideAgGridHook";

const PAGE_SIZE = 25;

const STATUS_LABELS = {
  success: { icon: "✓", color: "#16A34A" },
  not_upgraded: { icon: "⚠", color: "#DC2626" },
  version_mismatch: { icon: "⚡", color: "#D97706" },
  over_pushed: { icon: "🔄", color: "#EA580C" },
  pushed: { icon: "📤", color: "#2563EB" },
  abandoned: { icon: "🚫", color: "#6B7280" },
  expired: { icon: "⏰", color: "#7C3AED" },
};

const statusCell = (params) => {
  if (!params.value) return "";
  const meta = STATUS_LABELS[params.value] || { icon: "", color: "#6B7280" };
  const label = params.value
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  return `<span style="color:${meta.color};font-weight:600;font-size:12px;">${meta.icon} ${label}</span>`;
};

const dateCell = (params) => {
  if (!params.value) return '<span style="color:var(--ag-secondary-foreground-color);">-</span>';
  const [date, time] = String(params.value).split("T");
  return `<div>
    <div>${date || params.value}</div>
    <div style="font-size:12px;color:var(--ag-secondary-foreground-color);">${(time || "").split(".")[0]}</div>
  </div>`;
};

const badge = (bg, color) => (params) =>
  params.value == null || params.value === ""
    ? ""
    : `<span style="background:${bg};color:${color};padding:1px 8px;border-radius:9999px;font-size:12px;font-weight:500;">${params.value}</span>`;

const AppUpgradeStatusesAgGridHook = {
  mounted() {
    if (!window.agGrid || !window.agGrid.createGrid) {
      console.error("AppUpgradeStatusesAgGridHook: agGrid not found on window");
      return;
    }

    const self = this;

    const actionsRenderer = (params) => {
      if (!params.data) return "";
      const btn = document.createElement("button");
      btn.textContent = "Delete";
      btn.style.cssText = "color:#DC2626;font-size:12px;cursor:pointer;background:none;border:none;padding:0;";
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (window.confirm("Delete this device status?")) {
          self.pushEvent("delete", { id: String(params.data.id) });
        }
      });
      return btn;
    };

    const columnDefs = [
      { headerName: "Device Serial", field: "device_sn", flex: 1, minWidth: 150 },
      { headerName: "Vendor", field: "vendor", width: 110 },
      { headerName: "Model", field: "model", width: 110 },
      { headerName: "App Version", field: "app_version", width: 130, cellRenderer: badge("#DBEAFE", "#1E40AF") },
      { headerName: "Data Version", field: "data_version", width: 130, cellRenderer: badge("#DCFCE7", "#166534") },
      { headerName: "System Version", field: "system_version", width: 140, cellRenderer: badge("#EDE9FE", "#6B21A8") },
      { headerName: "Status", field: "status", width: 150, cellRenderer: statusCell },
      { headerName: "Remark", field: "remark", flex: 1, minWidth: 140, sortable: false },
      { headerName: "Finish Time", field: "finish_time", width: 160, cellRenderer: dateCell },
      { headerName: "Pushed Time", field: "pushed_time", width: 160, cellRenderer: dateCell },
      {
        headerName: "Actions",
        colId: "actions",
        pinned: "right",
        width: 100,
        sortable: false,
        filter: false,
        floatingFilter: false,
        resizable: false,
        cellRenderer: actionsRenderer,
      },
    ];

    this.gridApi = mountServerSideGrid(this, {
      columnDefs,
      rowsEvent: "app_upgrade_statuses:rows",
      refreshEvent: "app_upgrade_statuses:refresh",
      pageSize: PAGE_SIZE,
    });
  },

  destroyed() {
    if (this.gridApi) {
      this.gridApi.destroy();
      this.gridApi = null;
    }
  },
};

export default AppUpgradeStatusesAgGridHook;
